import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { useCallback, useEffect } from "react";
import { UploadDto, bffApi } from "../../../autogen/bff-api";
import { useApiError } from "../../errors/useApiError";
import { getPusher } from "../../pusher";
import { useAppDispatch } from "../../redux/hooks";

export type DocumentUploadContext =
  | "SourcingEvent"
  | "SourcingEventV2"
  | "Bid"
  | "BidV2"
  | "Contract"
  | "ContractMessageAttachment"
  | "Project"
  | "NegotiationRound"
  | "NegotiationRoundV2"
  | "Need"
  | "BidV2Rejection"
  | "BidV2QualificationEvidence"
  | "SeV2QuestionAttachment"
  | "SeV2AnswerAttachment"
  | "SeV2AnnouncementAttachment";

export const useDocumentsUploader = ({
  entityId,
  eventId,
  entityType,
  refetch,
  qualificationCriterionKey,
  questionId,
}: {
  entityType: DocumentUploadContext;
  entityId: string;
  eventId?: string;
  refetch: () => void;
  qualificationCriterionKey?: string;
  questionId?: string;
}) => {
  const dispatch = useAppDispatch();
  const displayer = useApiError();

  useEffect(() => {
    const channel = getPusher().subscribe(entityId);
    channel.bind("document-added", refetch);

    return () => {
      channel.unbind("document-added", refetch);
    };
  }, [entityId, refetch]);

  const createPostRequest = useCallback(
    async (file: {
      id: string;
      name: string;
      extension: string;
      file: File;
    }): Promise<
      { data: UploadDto } | { error: FetchBaseQueryError | SerializedError }
    > => {
      switch (entityType) {
        case "Bid": {
          const response = dispatch(
            bffApi.endpoints.createUploadForBseBid.initiate({
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "BidV2QualificationEvidence": {
          if (!qualificationCriterionKey)
            throw new Error("Missing qualification criterion key");
          const response = dispatch(
            bffApi.endpoints.createUploadForBidV2QualificationEvidence.initiate(
              {
                bidId: entityId,
                uploadId: file.id,
                createBidV2QualificationEvidenceUploadRequestDto: {
                  fileName: file.name,
                  fileSize: file.file.size,
                  fileExtension: file.extension,
                  mimeType: file.file.type,
                  criterionKey: qualificationCriterionKey,
                },
              },
            ),
          );
          response.reset();
          return await response;
        }
        case "BidV2Rejection": {
          if (!eventId) throw new Error("Missing event id");
          const response = dispatch(
            bffApi.endpoints.createUploadForBidV2Rejection.initiate({
              eventId,
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "NegotiationRoundV2": {
          if (!eventId) throw new Error("Missing event id");
          const response = dispatch(
            bffApi.endpoints.createUploadForNegotiationRoundV2.initiate({
              eventId,
              roundId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "BidV2": {
          const response = dispatch(
            bffApi.endpoints.createUploadForBidV2.initiate({
              bidId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Contract": {
          const response = dispatch(
            bffApi.endpoints.createUploadForContract.initiate({
              contractId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "ContractMessageAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForContractMessageAttachment.initiate({
              contractId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2QuestionAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSeV2QuestionAttachment.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2AnswerAttachment": {
          if (!questionId) throw new Error("Missing question id");
          const response = dispatch(
            bffApi.endpoints.createUploadForSeV2AnswerAttachment.initiate({
              eventId: entityId,
              questionId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SeV2AnnouncementAttachment": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSeV2AnnouncementAttachment.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SourcingEvent": {
          const response = dispatch(
            bffApi.endpoints.createUploadForBse.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "SourcingEventV2": {
          const response = dispatch(
            bffApi.endpoints.createUploadForSourcingEventV2.initiate({
              eventId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "NegotiationRound": {
          const response = dispatch(
            bffApi.endpoints.createUploadForNegotiationRound.initiate({
              negotiationRoundId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Project": {
          const response = dispatch(
            bffApi.endpoints.createUploadForProject.initiate({
              projectId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
        case "Need": {
          const response = dispatch(
            bffApi.endpoints.createUploadForNeed.initiate({
              needId: entityId,
              uploadId: file.id,
              createUploadRequestDto: {
                fileName: file.name,
                fileSize: file.file.size,
                fileExtension: file.extension,
                mimeType: file.file.type,
              },
            }),
          );
          response.reset();
          return await response;
        }
      }
    },
    [dispatch, entityId, entityType, eventId, qualificationCriterionKey, questionId],
  );

  const upload = useCallback(
    async (file: {
      id: string;
      name: string;
      extension: string;
      file: File;
    }): Promise<string | null> => {
      const postRequestResult = await createPostRequest(file);

      if ("data" in postRequestResult) {
        const data = postRequestResult.data;
        const form = new FormData();

        form.append("x-amz-date", data.renamedXAmzDate);
        form.append("x-amz-signature", data.renamedXAmzSignature);
        form.append("x-amz-algorithm", data.renamedXAmzAlgorithm);
        form.append("x-amz-credential", data.renamedXAmzCredential);
        form.append("policy", data.policy);
        form.append("key", data.key);
        form.append("file", file.file);

        // The S3 POST policy is signed for data.url; any other base URL gets a 403.
        const response = await fetch(data.url, {
          method: "POST",
          body: form,
        });

        if (!response.ok) {
          displayer.trigger({
            message: `could not perform upload, response: ${response.body}`,
          });
          return null;
        }

        return data.key;
      } else {
        displayer.trigger(postRequestResult.error);
        return null;
      }
    },
    [createPostRequest, displayer],
  );

  return {
    upload,
    createPostRequest,
  };
};
